import {MeterAssignment} from '../types';

// effectiveFrom / effectiveTo are AWSDateTime! on the backend — AppSync requires a
// strict ISO-8601 string. The FormikDateTimePicker (Mantine DateTimePicker) emits a
// JS Date object (or a non-ISO display string), which AppSync rejects with
// "Variable 'effectiveFrom' has an invalid value." Coerce to ISO-8601 here.
// Throws on an unparseable value so the mutation surfaces a clear error rather than
// sending garbage.
export const toAWSDateTime = (value: unknown): string => {
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        // Already a valid ISO-8601 / AWSDateTime string → pass through unchanged.
        // Otherwise (e.g. a locale display string from the picker), parse + normalize.
        const d = new Date(trimmed);
        if (isNaN(d.getTime())) {
            throw new Error(`Invalid effective date/time: "${value}"`);
        }
        return d.toISOString();
    }
    throw new Error(`Invalid effective date/time: ${String(value)}`);
};

// channelIdentifiers is an AWSJSON scalar on the wire — AppSync expects a
// JSON-encoded STRING, not an object. The edit form holds it as either a parsed
// object (round-tripped from a read) or a JSON text string (typed into the
// textarea). normalizeChannelIdentifiers coerces both to the canonical JSON
// string form the mutation variables require. Invalid JSON throws so the caller
// (mutation) surfaces the error rather than silently sending garbage.
export const normalizeChannelIdentifiers = (value: unknown): string => {
    if (typeof value === 'string') {
        // Validate it parses; re-stringify to canonicalize whitespace.
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed);
    }
    if (value && typeof value === 'object') {
        return JSON.stringify(value);
    }
    // Treat empty/undefined as an empty object.
    return JSON.stringify({});
};

// Builds the variables payload for the createMeterAssignment mutation.
// Whitelists the fields declared on backend CreateMeterAssignmentInput
// (meter-assignments.graphql) so frontend-only fields (createdBy) and
// server-managed fields (id, sortKey, updated, createdByUser) cannot leak into
// the mutation variables and trigger AppSync's "field not defined for input
// object type" validation error (FNP-33 pattern).
// FNP-217: sortKey is server-derived (= meterId#channel) and MUST NOT be sent.
export const buildCreateMeterAssignmentInput = (values: MeterAssignment): Record<string, unknown> => {
    const input: Record<string, unknown> = {
        customerId: values.customerId,
        meterId: values.meterId,
        channel: values.channel,
        channelIdentifiers: normalizeChannelIdentifiers(values.channelIdentifiers),
        status: values.status,
        effectiveFrom: toAWSDateTime(values.effectiveFrom),
    };
    if (values.freeTierQuantity !== undefined && values.freeTierQuantity !== null) {
        input.freeTierQuantity = values.freeTierQuantity;
    }
    if (values.effectiveTo !== undefined && values.effectiveTo !== '' && values.effectiveTo !== null) {
        input.effectiveTo = toAWSDateTime(values.effectiveTo);
    }
    if (values.createdByUsername !== undefined) input.createdByUsername = values.createdByUsername;
    if (values.created !== undefined) input.created = values.created;
    return input;
};

// Builds the variables payload for the updateMeterAssignment mutation.
// Whitelists the fields declared on backend UpdateMeterAssignmentInput; includes
// id (required) and updated. sortKey is server-derived and excluded.
export const buildUpdateMeterAssignmentInput = (values: MeterAssignment): Record<string, unknown> => {
    const input: Record<string, unknown> = {
        id: values.id,
        customerId: values.customerId,
        meterId: values.meterId,
        channel: values.channel,
        channelIdentifiers: normalizeChannelIdentifiers(values.channelIdentifiers),
        status: values.status,
        effectiveFrom: toAWSDateTime(values.effectiveFrom),
    };
    if (values.freeTierQuantity !== undefined && values.freeTierQuantity !== null) {
        input.freeTierQuantity = values.freeTierQuantity;
    }
    if (values.effectiveTo !== undefined && values.effectiveTo !== '' && values.effectiveTo !== null) {
        input.effectiveTo = toAWSDateTime(values.effectiveTo);
    }
    if (values.updated !== undefined) input.updated = values.updated;
    return input;
};

// Builds the variables payload for the deleteMeterAssignment mutation. FNP-217:
// the table is composite-key, so delete is by PK customerId + SK sortKey, NOT id.
export const buildDeleteMeterAssignmentInput = (values: Pick<MeterAssignment, 'customerId' | 'sortKey'>): Record<string, unknown> => ({
    customerId: values.customerId,
    sortKey: values.sortKey,
});
